import { forwardRef, InputHTMLAttributes, ReactNode, useImperativeHandle } from 'react'
import { useTextField } from '@react-aria/textfield'
import { useObjectRef } from 'utils/useObjectRef'
import { useField } from 'formik'
import cx from 'classnames'
import Styles from './TextField.module.scss'
import { FieldHookConfig } from 'formik/dist/Field'
import { TextFieldProps, TextFieldWithCounterProps, useTextFieldCounter } from './useTextFieldCounter'
import { useScrollToError } from './ScrollToError'

export const TextAreaField = forwardRef((props: TextFieldWithCounterProps<HTMLTextAreaElement>, forwardedRef) => {
  const { onChange, rows = 2, errorMessage, ...passedProps } = props
  const ref = useObjectRef<HTMLTextAreaElement>()
  useImperativeHandle(forwardedRef, () => ref.current, [ref])
  const { labelProps, inputProps, errorMessageProps, isInvalid, validationErrors, validationDetails } = useTextField(
    { ...passedProps, inputElementType: 'textarea' },
    ref,
  )

  // the event is swallowed by the onChange handler, we want to expose this
  const { onChange: ariaOnChange, ...ariaInputProps } = inputProps as InputHTMLAttributes<HTMLTextAreaElement>

  let errorMessageString: ReactNode = null
  if (typeof errorMessage === 'function') {
    errorMessageString =
      isInvalid && validationErrors != null && validationDetails != null
        ? errorMessage({
            isInvalid: true,
            validationErrors,
            validationDetails,
          })
        : null
  } else {
    errorMessageString = errorMessage
  }

  return (
    <div className={cx(Styles.textField, props.className)}>
      {props.label && (
        <label className={Styles.label} {...labelProps}>
          {props.label} {props.counter && <span>{props.counter}</span>}
        </label>
      )}
      <div
        className={cx(Styles.inputWrapper, Styles.textAreaWrapper, props.wrapperClassName, {
          [Styles.error]: props.errorMessage,
        })}
      >
        <textarea
          className={cx(Styles.input, props.inputClassName, {
            [Styles.error]: props.errorMessage,
          })}
          {...ariaInputProps}
          onChange={onChange}
          ref={ref}
          rows={rows}
        />
      </div>
      {errorMessage && (
        <div {...errorMessageProps} className={Styles.errorMessage}>
          {errorMessageString}
        </div>
      )}
    </div>
  )
})

export const TextAreaFieldWithCounter = forwardRef((props: TextFieldProps<HTMLTextAreaElement>, ref) => {
  const fieldProps = useTextFieldCounter(props)
  return <TextAreaField {...fieldProps} ref={ref} />
})

interface FormikTextAreaFieldProps {
  withCounter?: boolean
  label: string
  rows?: number
}

export const FormikTextAreaField = ({
  withCounter = false,
  ...props
}: FieldHookConfig<string> & FormikTextAreaFieldProps) => {
  const [field, meta] = useField(props)
  const displayError = meta.touched && meta.error
  const { inputRef } = useScrollToError(props.name)
  const enhancedProps = {
    ...props,
    ...field,
    ...(displayError && { errorMessage: meta.error }),
  }
  const Component = withCounter ? TextAreaFieldWithCounter : TextAreaField
  // @ts-ignore
  return <Component ref={inputRef} {...enhancedProps} />
}
